import { useMemo } from "react";
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { BaseWidget } from "./BaseWidget";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import { presenceBreakdown } from "../../../../helpers/personas/presenceAttendanceStats";
import MetricVisualization from "../../../dataVisualizations/MetricVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import templatePersonaNotice from "./_shared/templatePersonaNotice";
import NO_PERSONA_TEXT from "./_shared/noPersonaText";
import usePersonaPresenceData from "./_shared/usePersonaPresenceData";
import usePublicPersona from "./_shared/usePublicPersona";

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T6)
// Aanwezigheid van ÉÉN deelnemer (PERSONA_WIDGET_TYPES.PRESENCE) over de
// periode van het rapport of de widget. Niet te verwarren met
// PersonasPresenceWidget: dat is de hele organisatie, per maand.
//
// Vier toestanden die elk iets anders zeggen: laden, fout, leeg en de telling.
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
// AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure

const COPY = {
    noPersona: NO_PERSONA_TEXT,
    error: 'De aanwezigheid kon niet worden geladen.',
    empty: 'Geen aanwezigheid geregistreerd in deze periode.',
};

/**
 * @param {object} props
 * @param {string} props.message - De melding
 * @param {string} props.testId - data-testid, om de toestanden uit elkaar te houden
 * @returns {JSX.Element}
 */
const Message = ({ message, testId }) => (
    <div className="module-placeholder" data-testid={testId}>
        <p className="module-placeholder-message">{message}</p>
    </div>
);

/**
 * @extends {BaseWidget}
 */
export class PersonaPresenceWidget extends BaseWidget {

    getName() { return 'Aanwezigheid deelnemer'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <EventAvailableOutlinedIcon />; }

    getControls() {
        return {
            'period': new PeriodOverrideControl({
                label: 'Periode',
                description: 'Over welke periode de aanwezigheid wordt geteld.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {{ mode?: string, start: string|null, end: string|null }} [props.period] - Periode-instelling (PeriodOverrideControl)
 * @returns {JSX.Element}
 */
const Renderer = ({ period }) => {
    const { personasModuleOn, personaId, templateStatus } = usePublicPersona();
    const { startDate, endDate } = useWidgetPeriod(period);

    const { reports, loaded, error } = usePersonaPresenceData(personaId, startDate, endDate, {
        enabled: personasModuleOn,
    });

    const breakdown = useMemo(() => presenceBreakdown(reports), [reports]);

    const notice = templatePersonaNotice(templateStatus);
    if (notice) return notice;
    if (!personasModuleOn) return <ModulePlaceholder mode="disabled" moduleLabel="Deelnemers" />;
    if (!personaId) return <Message message={COPY.noPersona} testId="persona-presence-no-persona" />;
    if (error) return <Message message={COPY.error} testId="persona-presence-error" />;
    if (!loaded) {
        return (
            <div data-testid="persona-presence-loading">
                <MetricVisualization value={null} label="aanwezig" isLoading />
            </div>
        );
    }
    if (breakdown.total === 0) return <Message message={COPY.empty} testId="persona-presence-empty" />;

    return (
        <MetricVisualization
            value={breakdown.percentage}
            formatValue={(v) => `${v}%`}
            label="aanwezig"
            sublabel={`${breakdown.present} aanwezig · ${breakdown.absent} afwezig · ${breakdown.sick} ziek`}
        />
    );
};
